import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { ApolloError, useMutation } from '@apollo/client';
import * as React from 'react';
import ButtonLoading from 'Theme/components/ButtonLoading';
import TestIds from 'Tests/TestIds';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import Alert from 'Components/Shared/Alert/Alert';
import Authentication from 'Tools/auth';
import { DeleteMe } from 'Apollo/mutations/person/DeleteMe';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const RemoveProfileDialog = ({ isOpen, onClose }: Readonly<Props>) => {
  const ts = useTranslate();
  const { onError, onSuccess } = useNotification({ domain: 'user' });

  const onCompleted = () => {
    onSuccess(
      'page.userProfile.personalInformations.deleteMyProfile.deleteMeSuccess'
    );
    Authentication.deletedProfileLogout();
  };

  const onMutationError = (error: ApolloError) => {
    onError(error);
    if (error.graphQLErrors == null) {
      return;
    }
  };

  const [onRemoveMe, { loading }] = useMutation<any>(
    DeleteMe,
    {
      onError: onMutationError,
      onCompleted
    }
  );

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    onRemoveMe();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={onSubmit}>
        <DialogTitle>
          {ts(
            'page.userProfile.personalInformations.deleteMyProfile.confirmation'
          )}
        </DialogTitle>
        <DialogContent>
          <Alert
            severity="warning"
            title={ts('component.userAccountUpdate.irreversibleAction')}
            content={ts(
              'page.userProfile.personalInformations.deleteMyProfile.confirmationSentence'
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            {ts('common.action.cancel')}
          </Button>
          <ButtonLoading
            data-testid={
              TestIds.pages.userProfile.personalInfo.deleteConfirmationButton
            }
            type="submit"
            color="primary"
            loading={loading}
          >
            {ts('page.userProfile.personalInformations.deleteMyProfile.button')}
          </ButtonLoading>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RemoveProfileDialog;
